// deps
import React, { useMemo, memo, useCallback, useEffect, SVGProps, ReactNode } from "react";

// internl-deps
import { getAddressInfo, useEffectOnce } from "@swiggy-private/core-ui";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { default as HomeIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/homeFilled/16";
import { default as HomeNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/homeFilledNotification/16";
import { default as LocationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilled/16";
import { default as LocationNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilledNotification/16";
import { default as WorkIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/workFilled/16";
import { default as WorkNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/workFilledNotification/16";
import { ADDRESS_CATEGORIES } from "@swiggy-private/core-types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// helpers
import { isIrctcCart } from "@cart-ui/helpers/IrctcCart";
import { getHeaderAddressAnnotationText, mapAddressCategoryToAddressType } from "@cart-ui/helpers/CartHeader";

//constants
import { ACCESSIBILITY_TEXTS } from "@cart-ui/constants";

// widgets
import {
    AnnotationContainerStyled,
    AddressIconStyled,
    ContainerStyled,
    SubtitleStyled,
    TitleStyled,
    AddressInfoContainerStyled,
    SeparatorStyled,
    AddressInfoStyled,
    ChevronRightIconStyled,
    StyledDeliveryAddressIcon,
    ContentStyled,
    TitleTextStyled,
    CartHeaderContentContainer,
    IrctcIconStyled,
} from "./CartHeaderContent.style";
import { CartHeaderOptions } from "./components";
import { isEmpty } from "@swiggy-private/js-utils";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { hasInvalidAddressCategory } from "@cart-ui/helpers/CartFooter";
import { IIrctcPayload, UserAddress } from "@swiggy-private/cart-types";

export interface CartHeaderContentProps {
    isPageScrolled?: boolean;
    restaurantName: ReactNode;
    handleRestaurantNameClick(): void;
    selectedAddress?: UserAddress | null;
    onAddressDropdownClick?: () => void;
    qFoodSLA?: string;
    onImpressionCartHeaderContent: () => void;
    onClickCartHeaderDropdown: () => void;
    irctcData?: IIrctcPayload;
    showGreenSubtitle?: boolean;
    shouldPlay: boolean;
    cartIncognitoModeXp?: boolean;
    onImpressionToggleSwitch: (isEnabled: boolean) => void;
    isWithinSessionLimit: boolean;
    isIncognitoCart?: boolean;
    onToggleClick: VoidFunction;
    updateStatusBarCleanup: () => void;
    isBoltCart?: boolean;
    isLottieAnimation: boolean;
    isPopCart?: boolean;
    themeChange?: boolean;
    isOtg?: boolean;
    isBolt?: boolean;
    hasStamp?: boolean;
    lottieThemeChange?: boolean;
    showAddressNudgeIcon?: boolean;
    is99Store?: boolean;
    isGiftingCart?: boolean;
}

const DeliveryAddressIcon = memo(function DeliveryAddressIcon({
    category,
    shouldApplyInverseColor,
    showAddressNudgeIcon,
}: {
    category?: number;
    shouldApplyInverseColor?: boolean;
    showAddressNudgeIcon?: boolean;
}) {
    let deliveryIcon: (props: SVGProps<SVGSVGElement>) => JSX.Element;

    switch (category) {
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_HOME:
            deliveryIcon = showAddressNudgeIcon ? HomeNotificationIcon : HomeIcon;
            break;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_WORK:
            deliveryIcon = showAddressNudgeIcon ? WorkNotificationIcon : WorkIcon;
            break;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_FRIENDS_AND_FAMILY:
            deliveryIcon = showAddressNudgeIcon ? HomeNotificationIcon : HomeIcon;
            break;
        default:
            deliveryIcon = showAddressNudgeIcon ? LocationNotificationIcon : LocationIcon;
            break;
    }
    return (
        <StyledDeliveryAddressIcon
            data-testid={`${COMMON_HEADER_TEST_IDS.DELIVERY_ADDRESS_ICON}_${mapAddressCategoryToAddressType(category || 0)}`}
            Icon={deliveryIcon}
            fillColor={shouldApplyInverseColor ? "text_inverse_Highest_Emphasis" : "text_Highest_Emphasis"}
            strokeColor={shouldApplyInverseColor ? "text_One_Blck" : "text_inverse_Highest_Emphasis"}
        />
    );
});

const CartHeaderContentV2 = ({
    restaurantName,
    selectedAddress,
    onAddressDropdownClick,
    onImpressionCartHeaderContent,
    onClickCartHeaderDropdown,
    irctcData,
    cartIncognitoModeXp,
    onImpressionToggleSwitch,
    isIncognitoCart,
    onToggleClick,
    isPopCart,
    themeChange,
    isOtg,
    isLottieAnimation,
    isBolt,
    showAddressNudgeIcon,
    hasStamp,
    lottieThemeChange,
    is99Store,
    isGiftingCart,
}: CartHeaderContentProps) => {
    const addressInfo = useMemo(() => getAddressInfo(selectedAddress), [selectedAddress]);
    const category = useMemo(() => {
        if (hasInvalidAddressCategory(selectedAddress)) {
            return selectedAddress?.addressCategory;
        }
        return selectedAddress?.userIntentAddressCategory;
    }, [selectedAddress]);

    useEffectOnce(() => {
        onImpressionCartHeaderContent();
    });

    useEffect(() => {
        cartIncognitoModeXp && onImpressionToggleSwitch(isIncognitoCart || false);
    }, [cartIncognitoModeXp, onImpressionToggleSwitch, isIncognitoCart]);

    const isIrctcHeader = useMemo(() => isIrctcCart(irctcData), [irctcData]);

    const shouldApplyInverseColor = useMemo(
        // for 99 store it will be normal colors only
        () => (themeChange || lottieThemeChange) && !is99Store,
        [themeChange, is99Store, lottieThemeChange],
    );

    const onDropdownClick = useCallback(() => {
        onAddressDropdownClick?.();
        onClickCartHeaderDropdown();
    }, [onAddressDropdownClick, onClickCartHeaderDropdown]);

    const renderAddressAndSla = useMemo(() => {
        return (
            <>
                <AnnotationContainerStyled>
                    {!isIrctcHeader ? (
                        <DeliveryAddressIcon
                            category={category}
                            shouldApplyInverseColor={shouldApplyInverseColor}
                            showAddressNudgeIcon={showAddressNudgeIcon}
                        />
                    ) : (
                        <IrctcIconStyled fillColor={shouldApplyInverseColor ? "#FFFFFF99" : ""} />
                    )}
                    <Text
                        data-testid={COMMON_HEADER_TEST_IDS.ADDRESS_ANNOTATION_TEXT}
                        color={shouldApplyInverseColor ? "text_inverse_Highest_Emphasis" : "text_Highest_Emphasis"}
                        variant="V2.body2.neutral.bold"
                    >
                        {hasInvalidAddressCategory(selectedAddress)
                            ? getHeaderAddressAnnotationText(selectedAddress, category)
                            : selectedAddress?.annotation || getHeaderAddressAnnotationText(selectedAddress, category)}
                    </Text>
                </AnnotationContainerStyled>
                {!isEmpty(addressInfo) && (
                    <AddressInfoContainerStyled>
                        <SeparatorStyled shouldApplyInverseColor={shouldApplyInverseColor}>|</SeparatorStyled>
                        <AddressInfoStyled
                            variant="V2.body3.neutral.regular"
                            color={shouldApplyInverseColor ? "text_inverse_Low_Emphasis" : "text_Low_Emphasis"}
                        >
                            {addressInfo}
                        </AddressInfoStyled>
                    </AddressInfoContainerStyled>
                )}
            </>
        );
    }, [selectedAddress, showAddressNudgeIcon, addressInfo, shouldApplyInverseColor, isIrctcHeader, category]);

    const renderTitle = useMemo(() => {
        let color: keyof IColors = "text_Low_Emphasis";
        if (shouldApplyInverseColor) {
            color = "text_inverse_Low_Emphasis";
        }

        return (
            <TitleTextStyled color={color} variant="V2.body3.neutral.regular">
                {restaurantName}
            </TitleTextStyled>
        );
    }, [restaurantName, shouldApplyInverseColor]);

    const renderSubtitle = useMemo(() => {
        if (selectedAddress) {
            return renderAddressAndSla;
        }
    }, [renderAddressAndSla, selectedAddress]);

    const hideRxTitle = useMemo(() => {
        return isPopCart || isGiftingCart;
    }, [isPopCart, isGiftingCart]);

    return (
        <ContainerStyled>
            <CartHeaderContentContainer
                hasStamp={(lottieThemeChange && hasStamp) || (themeChange && (isOtg || isBolt))}
            >
                <ContentStyled>
                    {!hideRxTitle && (
                        <TitleStyled data-testid={COMMON_HEADER_TEST_IDS.RESTAURANT_NAME}>{renderTitle}</TitleStyled>
                    )}
                    <SubtitleStyled data-testid={COMMON_HEADER_TEST_IDS.DELIVERY_ADDRESS} onClick={onDropdownClick}>
                        {renderSubtitle}
                    </SubtitleStyled>
                </ContentStyled>

                {selectedAddress && (
                    <AddressIconStyled
                        data-testid={COMMON_HEADER_TEST_IDS.RIGHT_ICON}
                        aria-label={ACCESSIBILITY_TEXTS.CHANGE_ADDRESS}
                        onClick={onDropdownClick}
                    >
                        <ChevronRightIconStyled
                            strokeColor={shouldApplyInverseColor ? "#FFFFFF99" : ""}
                            strokeOpacity={0.6}
                            strokeWidth={2}
                        />
                    </AddressIconStyled>
                )}
            </CartHeaderContentContainer>

            <CartHeaderOptions
                cartIncognitoModeXp={cartIncognitoModeXp}
                isIncognitoCart={isIncognitoCart}
                onImpressionToggleSwitch={onImpressionToggleSwitch}
                onIncognitoToggleClick={onToggleClick}
                shouldApplyInverseColor={shouldApplyInverseColor}
                isLottieAnimation={isLottieAnimation}
            />
        </ContainerStyled>
    );
};

export default memo(CartHeaderContentV2);
